import {
  Box,
  Button,
  Container,
  Typography,
  Card,
  CardContent,
  Rating,
  Stack,
  Avatar,
} from '@mui/material';
import { StyledGrid as Grid } from '../components/StyledGrid';
import FacebookIcon from '@mui/icons-material/Facebook';
import GoogleIcon from '@mui/icons-material/Google';
import { inputReviews } from '../data/inputReviews';
import ParallaxHeader from '../components/ParallaxHeader';

interface Review {
  name: string;
  rating: number;
  review: string;
  date: string;
  platform: string;
  profilePic?: string;
}

const reviews: Review[] = inputReviews.map(r => ({
  name: r.author,
  rating: r.rating,
  review: r.text,
  date: r.date,
  platform: r.platform || 'facebook',
}));

// Aggregate stats
const totalReviews = reviews.length;
const avgRating = totalReviews > 0 ? (reviews.reduce((sum, r) => sum + r.rating, 0) / totalReviews) : 0;
const avgRatingRounded = Math.round(avgRating * 10) / 10;
const starEmojis = '★★★★★☆☆☆☆☆'.slice(5 - Math.round(avgRating), 10 - Math.round(avgRating));

// Helper to get initials (first and last)
function getInitials(name: string) {
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Helper to generate a color from initials (hash-based for consistency)
function getColorFromInitials(initials: string) {
  const colors = [
    '#F44336', // red
    '#E91E63', // pink
    '#9C27B0', // purple
    '#3F51B5', // indigo
    '#2196F3', // blue
    '#009688', // teal
    '#4CAF50', // green
    '#FF9800', // orange
    '#795548', // brown
    '#607D8B', // blue grey
  ];
  let hash = 0;
  for (let i = 0; i < initials.length; i++) {
    hash = initials.charCodeAt(i) + ((hash << 5) - hash);
  }
  const idx = Math.abs(hash) % colors.length;
  return colors[idx];
}

// Helper to extract year from date string
function getYear(dateStr: string): number {
  // Try to extract a 4-digit year
  const match = dateStr.match(/\d{4}/);
  if (match) return parseInt(match[0], 10);
  // Fallback: if only 2 digits, assume 20xx
  const match2 = dateStr.match(/\d{2}/);
  if (match2) return 2000 + parseInt(match2[0], 10);
  return new Date().getFullYear();
}

// Helper to get 'X years ago' or 'This year'
function getYearsAgo(year: number): string {
  const now = new Date().getFullYear();
  if (year === now) return 'This year';
  if (year > now) return `${year}`; // future-proof
  const diff = now - year;
  return diff === 1 ? 'Last year' : `${diff} years ago`;
}

// Sort reviews by year (newest first)
const sortedReviews = [...reviews].sort((a, b) => getYear(b.date) - getYear(a.date));

const Reviews = () => {
  return (
    <>
      <ParallaxHeader title="Reviews" imageUrl="/images/content/smiling-women.jpg" description="Here's what our amazing clients are saying" height={300} />
      <Box sx={{ py: 8, backgroundColor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
              {totalReviews} Reviews
            </Typography>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <span>{avgRatingRounded} / 5</span>
              <span role="img" aria-label="stars">{'★'.repeat(Math.round(avgRating))}{'☆'.repeat(5 - Math.round(avgRating))}</span>
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {sortedReviews.map((review, index) => (
              <Grid item xs={12} md={6} key={review.name}>
                <Card 
                  elevation={2}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        src={review.profilePic}
                        sx={{
                          width: 64,
                          height: 64,
                          mr: 2,
                          bgcolor: review.profilePic ? 'transparent' : getColorFromInitials(getInitials(review.name)),
                        }}
                      >
                        {!review.profilePic && getInitials(review.name)}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" component="div" gutterBottom>
                          {review.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Rating value={review.rating} readOnly size="small" sx={{ mr: 1 }} />
                          {review.platform === 'facebook' && (
                            <FacebookIcon color="primary" fontSize="small" />
                          )}
                          {review.platform === 'google' && (
                            <GoogleIcon color="error" fontSize="small" />
                          )}
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {getYearsAgo(getYear(review.date))}
                      </Typography>
                    </Box>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        mt: 2,
                      }}
                    >
                      {`“${review.review}”`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{
            mt: 8,
            mb: 0,
            borderRadius: 4,
            px: { xs: 2, md: 0 },
            py: { xs: 6, md: 8 },
            background: 'linear-gradient(90deg, #c97a1d 0%, #e6a100 50%, #b97a00 100%)', // darker orange/gold gradient
            color: 'white',
            textAlign: 'center',
            boxShadow: 3,
          }}>
            <Typography variant="h4" gutterBottom sx={{ color: 'white', fontWeight: 'bold', mb: 2 }}>
              Love Your Experience?
            </Typography>
            <Typography variant="body1" paragraph sx={{ maxWidth: 600, mx: 'auto', mb: 4, color: 'white', fontSize: '1.15rem' }}>
              We appreciate your feedback! Leave us a review on Facebook or Google to share your experience.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#c97a1d',
                  borderColor: '#c97a1d',
                  color: '#fff',
                  borderWidth: 3,
                  borderStyle: 'solid',
                  boxSizing: 'border-box',
                  fontWeight: 'bold',
                  minWidth: 200,
                  '&:hover, &:active': {
                    backgroundColor: '#b97a00',
                    borderColor: '#b97a00',
                    color: '#fff',
                  },
                }}
                startIcon={<FacebookIcon />}
                href="https://www.facebook.com/stylistinside/reviews"
                target="_blank"
              >
                Review on Facebook
              </Button>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#c97a1d',
                  borderColor: '#c97a1d',
                  color: '#fff',
                  borderWidth: 3,
                  borderStyle: 'solid',
                  boxSizing: 'border-box',
                  fontWeight: 'bold',
                  minWidth: 200,
                  '&:hover, &:active': {
                    backgroundColor: '#b97a00',
                    borderColor: '#b97a00',
                    color: '#fff',
                  },
                }}
                startIcon={<GoogleIcon />}
                href="https://share.google/OKlaFIBJXqHvHKNJB"
                target="_blank"
              >
                Review on Google
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Reviews;
